import mexp from 'math-expression-evaluator';
import React from 'react';
import Editor from 'react-simple-code-editor';

enum ExpressionValidOperators {
  VARIABLE_A = 'a',
  VARIABLE_B = 'b',
  ADDITION = '+',
  SUBTRACTION = '-',
  PARENS_OPEN = '(',
  PARENS_CLOSE = ')',
  MULTIPLICATION = '*',
  DIVISION = '/',
  // INVALID = '',
}

interface ITransformMap {
  [char: string]: boolean;
}

// allow uppercase variables (convert to lowercase before saving) and implied multiplication
export const transformMathFormula = (expression: string) => {
  const lowerCaseExpression = expression.toLocaleLowerCase().split('');

  const insertMultForVarMap = {
    'a(': true,
    'b(': true,
    ')a': true,
    ')b': true,
  } as ITransformMap;

  for (let i = 0; i < lowerCaseExpression.length; i++) {
    const char = lowerCaseExpression[i];
    const nextChar = lowerCaseExpression[i + 1];
    const insertMultForNumbers =
      (!Number.isNaN(parseInt(char, 10)) && nextChar === '(') ||
      (char === ')' && !Number.isNaN(parseInt(nextChar, 10)));

    // variables: eg. a( => a*(, )b => )*b OR numbers eg. 100( => 100*()
    if (insertMultForVarMap[`${char}${nextChar}`] || insertMultForNumbers) {
      lowerCaseExpression.splice(i + 1, 0, '*');
    }
  }
  return lowerCaseExpression.join('');
};

function validateExpressionOperators(m: string): ExpressionValidOperators | null {
  switch (m) {
    case 'a':
      return ExpressionValidOperators.VARIABLE_A;
    case 'b':
      return ExpressionValidOperators.VARIABLE_B;
    case '+':
      return ExpressionValidOperators.ADDITION;
    case '-':
      return ExpressionValidOperators.SUBTRACTION;
    case '(':
      return ExpressionValidOperators.PARENS_OPEN;
    case ')':
      return ExpressionValidOperators.PARENS_CLOSE;
    case '*':
      return ExpressionValidOperators.MULTIPLICATION;
    case '/':
      return ExpressionValidOperators.DIVISION;
  }
  return null;
}

interface IProps {
  value: string;
  highlight: (code: any) => any;
  padding: number;
  maxLength: number;
  onValueChange: (text: string) => any;
  textStyle?: object;
  wikiMsgHidden?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  width?: string;
  getExpressionEditorError?: (error: string) => any;
}

interface IState {
  warning: boolean;
  replaceWarning: boolean;
  warningMsg: string;
}

export class MathExpressionEditor extends React.Component<IProps, IState> {
  // State initialization
  state = {
    warning: false,
    replaceWarning: false,
    warningMsg: '',
    macrosReplacement: [],
  };

  // Constructor
  constructor(props: IProps) {
    super(props);
  }

  // Executed at each VAST input value change
  handleValueChange = (expression: string) => {
    if (!this.props.disabled) {
      this.checkExpressionValidity(expression);
      this.props.onValueChange(expression);
    }
  };

  // Check if Input Expression is valid
  checkExpressionValidity = (expression: string) => {
    // user can input uppercase A or B but will be converted to lowercase before saving
    const lowerCaseExpression = expression.toLocaleLowerCase();
    const [isValidExpression, error] = this.validateExpression(lowerCaseExpression);
    // send error outside of component
    if (this.props.getExpressionEditorError) {
      this.props.getExpressionEditorError(error);
    }

    if (!isValidExpression) {
      // this.props.getExpressionEditorError && this.props.getExpressionEditorError(error);
      this.setState({
        warning: true,
        replaceWarning: false,
        warningMsg: `WARNING: ${error}`,
      });
      return false;
    }
    this.setState({
      warning: false,
      replaceWarning: false,
      warningMsg: '',
    });
    return true;
  };

  // Return style for text input field
  getTextStyle = () => {
    const style = this.props.textStyle !== undefined ? this.props.textStyle : {};
    if (this.state.warning && !this.state.replaceWarning) {
      // Error style
      return { ...style, border: 'solid rgb(255, 165, 0, 0.05) 1px', background: '#fff5f5' };
    }
    // Normal style
    return style;
  };

  // Rendering
  render() {
    return (
      <div style={{ width: this.props.width || '100%' }}>
        {/* Math Expression text editor */}
        <div style={this.getTextStyle()}>
          <Editor
            placeholder={this.props.placeholder}
            value={this.props.value}
            highlight={this.props.highlight}
            padding={this.props.padding}
            maxLength={this.props.maxLength}
            onValueChange={this.handleValueChange}
            style={this.props.style}
          />
        </div>
        {/* Error message */}
        {this.state.warning && (
          <p
            style={{
              color: this.state.replaceWarning ? 'green' : 'orange',
              marginTop: '0px',
              marginBottom: '0px',
              fontWeight: 600,
              fontSize: '12px',
            }}
          >
            {this.state.warningMsg}
          </p>
        )}
        {/* Wiki message */}
        {!this.props.wikiMsgHidden && (
          <p style={{ marginTop: '0px', color: '#636363' }}>
            supported tokens are: <br />
            variables: <b>a b</b>, operators: <b>+ - * / ( )</b> <br />
            eg. ((a+b)*100)/2
          </p>
        )}
      </div>
    );
  }

  private validConsecutiveTokens(char: string, nextChar: string) {
    // can't have '1a', 'a1', '1b', 'b1', 'ab', 'ba', 'aa', 'bb' in expression

    // a1, b1
    if (
      (validateExpressionOperators(char) === 'a' || validateExpressionOperators(char) === 'b') &&
      !Number.isNaN(parseInt(nextChar, 10))
    ) {
      return false;
    }
    // 1a, 1b
    if (!Number.isNaN(parseInt(char, 10)) && (nextChar === 'a' || nextChar === 'b')) {
      return false;
    }
    // ab, ba
    if ((char === 'a' && nextChar === 'b') || (char === 'b' && nextChar === 'a')) {
      return false;
    }
    // aa, bb
    if ((char === 'a' && nextChar === 'a') || (char === 'b' && nextChar === 'b')) {
      return false;
    }
    return true;
  }

  private validateExpression(expression: string) {
    if (!expression) {
      return [true, ''];
    }

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      const nextChar = expression[i + 1];
      let error = '';
      // only a b + - * / ( ) are allowed
      if (!validateExpressionOperators(char) && Number.isNaN(parseInt(char, 10))) {
        error = `invalid character, ${char} is not allowed`;
        return [false, error];
      }
      // catch a simple divide by 0
      if (char === '/' && nextChar === '0') {
        error = `invalid expression ${char}${nextChar}, can't divide by 0!`;
        return [false, error];
      }
      // eg. ab not allowed, specify a*b
      if (!this.validConsecutiveTokens(char, nextChar)) {
        let error = `invalid expression ${char}${nextChar}, did you mean eg. ${char}*${nextChar} or ${char}/${nextChar}?`;
        // eg. a/b0 not allowed
        if (nextChar === '0') {
          error = `invalid expression ${char}${nextChar}.`;
        }
        return [false, error];
      }
    }

    // try a calculation on client side using the eval function. If it throws an error, then it's not a valid expression
    try {
      const METRIC_A_VAL = '100';
      const METRIC_B_VAL = '100';
      const varReplacedExpression = expression
        .replace(/a/g, METRIC_A_VAL)
        .replace(/b/g, METRIC_B_VAL);
      const evaluatedExpression = Number(mexp.eval(varReplacedExpression));

      if (isNaN(evaluatedExpression)) {
        throw new Error(`${evaluatedExpression} is not a number`);
      }
      return [!isNaN(evaluatedExpression), null];
    } catch (error) {
      // error logged here will be shown to user as well eg. ") is not allowed after ("
      return [false, error.message];
    }
  }
}
